import type { DrawingDocument, DrawingEntity, DrawingPage, Point } from '../contract/types.js';
import { escapeXml as esc, formatNumber as f } from '../overlay/format.js';
import {
  advanceIsEstimated,
  advanceOf,
  anchorOf,
  boundsOf,
  hitMarkup,
  shapeMarkup,
} from '../overlay/shapes.js';
import type { ChangeClass, DiffEntry, DiffResult, PageAlignmentEntry } from './diff.js';
import { diffEdges, edgeMessage, type DiffEdge } from './edges.js';

/**
 * The comparison overlay.
 *
 * One file per pair of aligned sheets, drawn from the structured comparison and
 * nothing else, in the original page points so it registers against the page as
 * a person sees it.
 *
 * **Colour is redundant here, and that is a requirement rather than a courtesy.**
 * Every class is carried three times over: by line type, by line weight, and by
 * the shape of its badge — a circle for geometry the new version has, a square
 * for geometry only the old version had, a diamond where the system declines to
 * decide. A reviewer who cannot separate the hues, or who prints the file, or
 * who opens it under forced colours, still reads the same six answers. The
 * encoding is exported as {@link DIFF_STYLE} and its distinctness is asserted in
 * the tests, because the way this requirement dies is a later well-meant edit
 * nudging two dash patterns together, which looks like nothing in review.
 *
 * Two structural decisions worth knowing:
 *
 *   * **The viewBox is the union of both sheets' page boxes**, and the before,
 *     after and diff views all use it. Registration between the three is then a
 *     property of the coordinate system rather than something to be tuned, and
 *     it survives zoom, scroll and a rotated page for the same reason.
 *   * **Nothing is referenced by identifier.** No `<defs>`, no `<use>`, no
 *     markers. The host inlines three of these into one HTML document, where
 *     `url(#id)` binds to the first match in the document and the two other
 *     views quietly borrow the first one's patterns. Drawing every mark
 *     explicitly costs bytes and removes that failure entirely.
 */

export interface DiffClassStyle {
  colour: string;
  strokeWidth: number;
  /** An empty string is a continuous line. */
  dasharray: string;
  linecap: 'butt' | 'round';
  badge: 'none' | 'circle' | 'square' | 'diamond';
  glyph: 'none' | 'plus' | 'minus' | 'delta' | 'arrow' | 'pair';
  /** The line type in words, which is what survives a greyscale photocopy. */
  patternName: { en: string; ja: string };
  label: { en: string; ja: string };
}

/**
 * The encoding, in one exported table so the drawing, the legend and the tests
 * cannot drift apart.
 *
 * The hues avoid green deliberately. Against the orange already used for
 * removal, green collapses under deuteranopia and protanopia — it is the
 * canonical confusion pair — so green stays a status colour in the application
 * and never becomes a change class.
 */
export const DIFF_STYLE: Record<ChangeClass, DiffClassStyle> = {
  added: {
    colour: '#0b7285',
    strokeWidth: 2.1,
    dasharray: '',
    linecap: 'butt',
    badge: 'circle',
    glyph: 'plus',
    patternName: { en: 'continuous thick', ja: '太い実線' },
    label: { en: 'added', ja: '追加' },
  },
  removed: {
    colour: '#d9480f',
    strokeWidth: 1.4,
    dasharray: '5.6 2.8',
    linecap: 'butt',
    badge: 'square',
    glyph: 'minus',
    patternName: { en: 'long dash', ja: '破線' },
    label: { en: 'removed', ja: '削除' },
  },
  changed: {
    colour: '#a61e4d',
    strokeWidth: 1.75,
    dasharray: '7 2.1 1.4 2.1',
    linecap: 'butt',
    badge: 'circle',
    glyph: 'delta',
    patternName: { en: 'dash-dot', ja: '一点鎖線' },
    label: { en: 'changed', ja: '変更' },
  },
  moved: {
    colour: '#4c6ef5',
    strokeWidth: 1.75,
    dasharray: '1.4 1.4',
    linecap: 'butt',
    badge: 'circle',
    glyph: 'arrow',
    patternName: { en: 'fine even dash', ja: '等間隔の細かい破線' },
    label: { en: 'moved', ja: '移動' },
  },
  ambiguous: {
    colour: '#14181d',
    strokeWidth: 2.1,
    dasharray: '0.7 4.9',
    linecap: 'round',
    badge: 'diamond',
    glyph: 'pair',
    patternName: { en: 'beaded', ja: '点鎖' },
    label: { en: 'ambiguous', ja: '判定不能' },
  },
  unchanged: {
    colour: '#8a949e',
    strokeWidth: 0.5,
    dasharray: '',
    linecap: 'butt',
    badge: 'none',
    glyph: 'none',
    patternName: { en: 'continuous thin', ja: '細い実線' },
    label: { en: 'unchanged', ja: '変更なし' },
  },
};

/**
 * Where something used to be. ISO 128 type K is the standard line type for the
 * original position of a part that has moved, so it is already read that way.
 */
const GHOST = {
  strokeWidth: 0.5,
  dasharray: '7 2.1 1.4 2.1 1.4 2.1',
  opacity: 0.55,
  patternName: { en: 'long dash double-dot', ja: '二点鎖線' },
  label: { en: 'was here', ja: '変更前' },
};

/** The order the board states the classes in, used everywhere they are listed. */
export const DIFF_LEGEND_ORDER: readonly ChangeClass[] = [
  'added',
  'removed',
  'changed',
  'moved',
  'ambiguous',
  'unchanged',
];

/** Facts the artefact carries so it can be read on its own, with no application. */
export interface DiffOverlayProvenance {
  baseArtefactSha256?: string;
  compareArtefactSha256?: string;
  matcherProfileVersion?: string;
  commit?: string;
}

/**
 * The frame all three views of one sheet are drawn in: the union of the two
 * versions' page boxes.
 *
 * Exported because the host draws the before and after views itself, through
 * `renderOverlay`'s `frame` option, and they only register with the difference
 * view if all three use the same box. Two copies of that rule would be two
 * rules, and the day they disagreed the three views would drift apart with
 * nothing to catch it — the old drawing would simply look slightly offset,
 * which reads as movement in the drawing rather than as a fault in the viewer.
 */
export function sheetFrame(
  base: DrawingDocument,
  compare: DrawingDocument,
  diff: DiffResult,
  sheetIndex: number,
): { widthPt: number; heightPt: number } {
  const sheet = diff.pageAlignment[sheetIndex];
  if (!sheet) throw new Error(`no such sheet: ${sheetIndex}`);
  const basePage = pageAt(base, sheet.basePageIndex);
  const comparePage = pageAt(compare, sheet.comparePageIndex);
  return {
    widthPt: Math.max(basePage?.widthPt ?? 0, comparePage?.widthPt ?? 0) || 1,
    heightPt: Math.max(basePage?.heightPt ?? 0, comparePage?.heightPt ?? 0) || 1,
  };
}

export function renderDiffOverlay(
  base: DrawingDocument,
  compare: DrawingDocument,
  diff: DiffResult,
  sheetIndex: number,
  provenance: DiffOverlayProvenance = {},
): string {
  if (diff.pageAlignment.length === 0) {
    throw new Error(
      'this comparison aligned no sheets; a diff overlay is drawn per aligned sheet, ' +
        'and the matcher profile that produced it does not align sheets at all',
    );
  }
  const sheet = diff.pageAlignment[sheetIndex];
  if (!sheet) throw new Error(`no such sheet: ${sheetIndex}`);

  const basePage = pageAt(base, sheet.basePageIndex);
  const comparePage = pageAt(compare, sheet.comparePageIndex);
  const { widthPt: width, heightPt: height } = sheetFrame(base, compare, diff, sheetIndex);
  const boxesDiffer =
    basePage !== null &&
    comparePage !== null &&
    (basePage.widthPt !== comparePage.widthPt || basePage.heightPt !== comparePage.heightPt);

  const baseIndex = entityIndex(basePage);
  const compareIndex = entityIndex(comparePage);
  const entries = diff.entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.sheetIndex === sheetIndex);

  const badgeRadius = Math.max(Math.hypot(width, height) / 200, 3);
  const edges = diffEdges(diff);

  const layers: Record<string, string[]> = {
    ghost: [],
    leader: [],
    unchanged: [],
    removed: [],
    moved: [],
    changed: [],
    added: [],
    ambiguous: [],
    badge: [],
    hit: [],
  };

  // An entry naming an entity the documents do not contain cannot be drawn. That
  // is a caller passing a comparison and a pair of documents that do not belong
  // together, and skipping it in silence would leave the legend counting a mark
  // the file does not carry. The count goes on the root, where a reader and a
  // test can both see it.
  let unresolved = 0;

  for (const { entry, index } of entries) {
    const baseEntity = entry.base ? baseIndex.get(entry.base.entityId) : undefined;
    const compareEntity = entry.compare ? compareIndex.get(entry.compare.entityId) : undefined;
    // The live mark is drawn from whichever version still holds the entity.
    const live = compareEntity ?? baseEntity;
    if (!live) {
      unresolved += 1;
      continue;
    }
    const liveSide = compareEntity ? 'compare' : 'base';
    const shared = dataAttributes(entry, index, live);
    const style = DIFF_STYLE[entry.changeClass];

    layers[entry.changeClass]!.push(
      shapeMarkup(
        live,
        `d2d-d d2d-d-${entry.changeClass}`,
        `d2d-t d2d-t-${entry.changeClass}`,
        `${shared} data-side="${liveSide}" data-role="geometry"`,
      ),
      ...textRule(live, entry.changeClass, `${shared} data-side="${liveSide}"`),
    );
    layers.hit!.push(hitMarkup(live, hitClass(live), `${shared} data-side="${liveSide}"`));

    // A ghost is the base geometry where the entity also still exists, so the
    // reviewer sees the edit rather than only its result. It lives in a layer
    // below every live mark, so it can never hide the geometry it explains.
    const ghosted =
      (entry.changeClass === 'changed' || entry.changeClass === 'moved') && baseEntity !== undefined;
    if (ghosted && baseEntity) {
      layers.ghost!.push(
        shapeMarkup(
          baseEntity,
          `d2d-ghost d2d-ghost-${entry.changeClass}`,
          `d2d-ghost-text d2d-ghost-${entry.changeClass}`,
          `${shared} data-side="base" data-role="ghost"`,
        ),
        ...ghostTextRule(baseEntity, entry.changeClass, `${shared} data-side="base"`),
      );
      layers.hit!.push(hitMarkup(baseEntity, hitClass(baseEntity), `${shared} data-side="base"`));
      layers.leader!.push(
        ...leader(baseEntity, live, entry, index, badgeRadius),
      );
    }

    if (style.badge !== 'none') {
      layers.badge!.push(
        badge(live, entry, index, badgeRadius, liveSide, { widthPt: width, heightPt: height }),
      );
    }
  }

  const counts = countByClass(entries.map((e) => e.entry));
  const parts: string[] = [];
  parts.push(openTag(diff, sheet, sheetIndex, width, height, boxesDiffer, unresolved, provenance));
  parts.push(title(diff, sheet, counts));
  parts.push(description(diff, sheet, provenance));
  parts.push(`<style>${styleSheet()}</style>`);
  parts.push(`<rect class="d2d-diff-paper" x="0" y="0" width="${f(width)}" height="${f(height)}"/>`);
  // The legend is painted here, **before** any geometry, so that it cannot hide
  // the drawing it explains. Painted last it was an opaque panel over a third of
  // the sheet's width, and the marks underneath it were simply gone. Behind the
  // drawing it stays readable wherever the sheet is empty, which in the corner it
  // sits in it generally is.
  parts.push(legend(diff, counts, edges, width, height));
  if (boxesDiffer && basePage) parts.push(pageBoxOutline(basePage, comparePage));
  for (const name of ['ghost', 'leader', 'unchanged', 'removed', 'moved', 'changed', 'added', 'ambiguous', 'badge', 'hit']) {
    const body = layers[name]!;
    if (body.length === 0) continue;
    parts.push(`<g class="d2d-diff-l-${name}">`, ...body, '</g>');
  }
  parts.push('</svg>');
  return parts.join('\n');
}

function pageAt(doc: DrawingDocument, index: number | null): DrawingPage | null {
  if (index === null) return null;
  return doc.pages.find((p) => p.index === index) ?? null;
}

function entityIndex(page: DrawingPage | null): Map<string, DrawingEntity> {
  const out = new Map<string, DrawingEntity>();
  for (const e of page?.entities ?? []) out.set(e.id, e);
  return out;
}

/**
 * The join back to the comparison.
 *
 * `data-entry` indexes `diff.json`, and it is the only reliable key: entity
 * identifiers are unique within one document but not across two, so the same
 * string names a different entity in the other version. An absent side is
 * omitted rather than emitted empty — an empty attribute and a missing one read
 * the same to a careless handler, and the difference between them is exactly
 * what added and removed mean.
 */
function dataAttributes(entry: DiffEntry, index: number, live: DrawingEntity): string {
  const bits = [
    ` data-entry="${String(index).padStart(4, '0')}"`,
    ` data-change-class="${entry.changeClass}"`,
    ` data-entity-type="${entry.entityType}"`,
    ` data-layer="${esc(live.layer)}"`,
  ];
  if (entry.base) {
    bits.push(` data-base-id="${esc(entry.base.entityId)}"`, ` data-base-page="${entry.base.pageIndex}"`);
  }
  if (entry.compare) {
    bits.push(
      ` data-compare-id="${esc(entry.compare.entityId)}"`,
      ` data-compare-page="${entry.compare.pageIndex}"`,
    );
  }
  if (entry.displacement !== undefined) bits.push(` data-displacement="${f(entry.displacement)}"`);
  return bits.join('');
}

function hitClass(e: DrawingEntity): string {
  return e.type === 'TEXT' || e.type === 'MTEXT' ? 'd2d-hit-box' : 'd2d-hit';
}

/**
 * The rule under a text run.
 *
 * Text is filled rather than stroked, so glyphs cannot carry a dash pattern at
 * all. Without this rule a changed string would be distinguished by colour and
 * nothing else, which is the one thing the encoding may not do. A removed
 * string is struck through along its own baseline instead, which is the
 * unambiguous mark for text that is gone.
 */
function textRule(e: DrawingEntity, changeClass: ChangeClass, attrs: string): string[] {
  if (e.type !== 'TEXT' && e.type !== 'MTEXT') return [];
  if (changeClass === 'unchanged') return [];
  const struck = changeClass === 'removed';
  const offset = struck ? -e.height * 0.3 : e.height * 0.18;
  const estimated = advanceIsEstimated(e) ? ' data-advance="estimated"' : '';
  return [
    `<line class="d2d-d d2d-d-${changeClass}"${attrs} data-role="${struck ? 'strike' : 'rule'}"${estimated}` +
      ` x1="${f(e.anchor.x)}" y1="${f(e.anchor.y + offset)}"` +
      ` x2="${f(e.anchor.x + advanceOf(e))}" y2="${f(e.anchor.y + offset)}"` +
      ` transform="rotate(${f(-e.rotation)} ${f(e.anchor.x)} ${f(e.anchor.y)})"/>`,
  ];
}

function ghostTextRule(e: DrawingEntity, changeClass: ChangeClass, attrs: string): string[] {
  if (e.type !== 'TEXT' && e.type !== 'MTEXT') return [];
  return [
    `<line class="d2d-ghost d2d-ghost-${changeClass}"${attrs} data-role="ghost-rule"` +
      ` x1="${f(e.anchor.x)}" y1="${f(e.anchor.y - e.height * 0.3)}"` +
      ` x2="${f(e.anchor.x + advanceOf(e))}" y2="${f(e.anchor.y - e.height * 0.3)}"` +
      ` transform="rotate(${f(-e.rotation)} ${f(e.anchor.x)} ${f(e.anchor.y)})"/>`,
  ];
}

/**
 * The line from where an entity was to where it is.
 *
 * Drawn at true length and never at a minimum, so it can never overstate a
 * displacement. A move ends in an arrowhead and a change does not: the
 * arrowhead is the mark that says *travelled* with no colour, no dash and no
 * words, and it is the only thing separating the two classes at a glance.
 */
function leader(
  from: DrawingEntity,
  to: DrawingEntity,
  entry: DiffEntry,
  index: number,
  badgeRadius: number,
): string[] {
  const a = anchorOf(from);
  const b = anchorOf(to);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length < 1e-6) return [];

  const attrs =
    ` data-entry="${String(index).padStart(4, '0')}" data-change-class="${entry.changeClass}"` +
    ` data-role="leader"`;
  const out = [
    `<line class="d2d-leader d2d-leader-${entry.changeClass}"${attrs}` +
      ` x1="${f(a.x)}" y1="${f(a.y)}" x2="${f(b.x)}" y2="${f(b.y)}"/>`,
  ];

  if (entry.changeClass === 'moved') {
    const size = Math.min(badgeRadius * 0.9, length);
    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    const tipX = b.x;
    const tipY = b.y;
    const baseX = b.x - ux * size;
    const baseY = b.y - uy * size;
    const half = size * 0.42;
    out.push(
      `<polygon class="d2d-head d2d-head-moved"${attrs}` +
        ` points="${f(tipX)},${f(tipY)} ${f(baseX - uy * half)},${f(baseY + ux * half)}` +
        ` ${f(baseX + uy * half)},${f(baseY - ux * half)}"/>`,
    );
  }
  return out;
}

/**
 * The badge: a filled silhouette carrying a glyph, set off the geometry it
 * annotates so it never sits on the ink.
 *
 * The silhouette is the cue that survives a zoom where the glyph is illegible —
 * circle for geometry the new version has, square for geometry only the old
 * version had, diamond where the system declines to decide.
 */
function badge(
  e: DrawingEntity,
  entry: DiffEntry,
  index: number,
  r: number,
  side: 'base' | 'compare',
  frame: { widthPt: number; heightPt: number },
): string {
  const [, y0, x1] = boundsOf(e);
  // Set off the entity's box, then held inside the sheet. Unclamped, a change on
  // an entity at the top or right edge — the frame, the title block, the top
  // line of a grid — put its badge outside the viewBox, where it simply is not
  // drawn: the marks that most need a badge were the ones that lost it.
  const cx = Math.min(Math.max(x1 + 1.6 * r, r + 1), frame.widthPt - r - 1);
  const cy = Math.min(Math.max(y0 - 1.6 * r, r + 1), frame.heightPt - r - 1);
  const style = DIFF_STYLE[entry.changeClass];
  const attrs =
    ` data-entry="${String(index).padStart(4, '0')}" data-change-class="${entry.changeClass}"` +
    ` data-role="badge" data-side="${side}"`;
  const g = r * 0.55;

  const outline =
    style.badge === 'circle'
      ? `<circle class="d2d-badge d2d-badge-${entry.changeClass}"${attrs} cx="${f(cx)}" cy="${f(cy)}" r="${f(r)}"/>`
      : style.badge === 'square'
        ? `<rect class="d2d-badge d2d-badge-${entry.changeClass}"${attrs} x="${f(cx - r)}" y="${f(cy - r)}" width="${f(2 * r)}" height="${f(2 * r)}"/>`
        : `<polygon class="d2d-badge d2d-badge-${entry.changeClass}"${attrs} points="${f(cx)},${f(cy - r)} ${f(cx + r)},${f(cy)} ${f(cx)},${f(cy + r)} ${f(cx - r)},${f(cy)}"/>`;

  return [
    `<g class="d2d-badge-group"${attrs}>`,
    `<title>${esc(badgeTitle(entry))}</title>`,
    outline,
    glyph(entry, cx, cy, g, attrs),
    '</g>',
  ].join('');
}

function badgeTitle(entry: DiffEntry): string {
  const where = [
    entry.base ? `base page ${entry.base.pageIndex + 1} ${entry.base.entityId}` : null,
    entry.compare ? `compare page ${entry.compare.pageIndex + 1} ${entry.compare.entityId}` : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' → ');
  const displaced = entry.displacement === undefined ? '' : ` · displaced ${f(entry.displacement)}`;
  return `${entry.changeClass} · ${entry.entityType} · ${where}${displaced}`;
}

function glyph(entry: DiffEntry, cx: number, cy: number, g: number, attrs: string): string {
  const style = DIFF_STYLE[entry.changeClass];
  const cls = `d2d-glyph d2d-glyph-${entry.changeClass}`;
  switch (style.glyph) {
    case 'plus':
      return `<path class="${cls}"${attrs} d="M ${f(cx - g)} ${f(cy)} H ${f(cx + g)} M ${f(cx)} ${f(cy - g)} V ${f(cy + g)}"/>`;
    case 'minus':
      return `<path class="${cls}"${attrs} d="M ${f(cx - g)} ${f(cy)} H ${f(cx + g)}"/>`;
    case 'delta':
      return `<polygon class="${cls}"${attrs} points="${f(cx)},${f(cy - g)} ${f(cx + g * 0.9)},${f(cy + g * 0.7)} ${f(cx - g * 0.9)},${f(cy + g * 0.7)}"/>`;
    case 'arrow': {
      // The glyph points the way the entity actually travelled, so the badge
      // alone carries the displacement's direction. The barbs are set at 145
      // degrees to the shaft and drawn as one stroke with it: shorter barbs, or
      // barbs drawn as separate strokes, collapse visually into a plain
      // diagonal bar, and a diagonal bar inside a circle is a prohibition sign.
      const heading = headingOf(entry);
      const barbLength = g * 0.8;
      const point = (angle: number, radius: number, from: Point): Point => ({
        x: from.x + Math.cos(angle) * radius,
        y: from.y + Math.sin(angle) * radius,
      });
      const centre = { x: cx, y: cy };
      const tip = point(heading, g, centre);
      const tail = point(heading + Math.PI, g, centre);
      const left = point(heading + 2.53, barbLength, tip);
      const right = point(heading - 2.53, barbLength, tip);
      return (
        `<path class="${cls}"${attrs} d="M ${f(tail.x)} ${f(tail.y)} L ${f(tip.x)} ${f(tip.y)}` +
        ` M ${f(left.x)} ${f(left.y)} L ${f(tip.x)} ${f(tip.y)} L ${f(right.x)} ${f(right.y)}"/>`
      );
    }
    case 'pair':
      return (
        `<circle class="${cls} d2d-glyph-filled"${attrs} cx="${f(cx - g * 0.5)}" cy="${f(cy)}" r="${f(g * 0.36)}"/>` +
        `<circle class="${cls} d2d-glyph-filled"${attrs} cx="${f(cx + g * 0.5)}" cy="${f(cy)}" r="${f(g * 0.36)}"/>`
      );
    case 'none':
      return '';
  }
}

/**
 * The direction of travel, from the recorded source boxes.
 *
 * Falls back to pointing right when the two boxes coincide, which happens when
 * an entity changed in place: the arrow glyph belongs to `moved`, and a moved
 * entity with no displacement is a contradiction the renderer should survive
 * rather than crash on.
 */
function headingOf(entry: DiffEntry): number {
  if (!entry.base || !entry.compare) return 0;
  const a = centreOfBox(entry.base.bbox);
  const b = centreOfBox(entry.compare.bbox);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return dx === 0 && dy === 0 ? 0 : Math.atan2(dy, dx);
}

function centreOfBox(bbox: readonly [number, number, number, number]): Point {
  return { x: (bbox[0] + bbox[2]) / 2, y: (bbox[1] + bbox[3]) / 2 };
}

function openTag(
  diff: DiffResult,
  sheet: PageAlignmentEntry,
  sheetIndex: number,
  width: number,
  height: number,
  boxesDiffer: boolean,
  unresolved: number,
  provenance: DiffOverlayProvenance,
): string {
  const bits = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${f(width)} ${f(height)}"`,
    ` width="${f(width)}" height="${f(height)}" preserveAspectRatio="xMidYMid meet"`,
    ` class="d2d-diff" data-sheet="${sheetIndex}"`,
    ` data-base-page="${sheet.basePageIndex === null ? 'none' : sheet.basePageIndex}"`,
    ` data-compare-page="${sheet.comparePageIndex === null ? 'none' : sheet.comparePageIndex}"`,
    ` data-sheet-similarity="${f(sheet.similarity)}"`,
    ` data-schema-version="${esc(diff.schemaVersion)}"`,
    ` data-matcher-profile-version="${esc(diff.matcherProfileVersion)}"`,
    ` data-comparison-space="${diff.comparisonSpace}" data-partial="${diff.partial}"`,
    ` data-tolerance="${f(diff.tolerance)}" data-displacement-unit="${diff.displacementUnit}"`,
    ` data-page-box-mismatch="${boxesDiffer}"`,
    ` data-unresolved-entries="${unresolved}"`,
  ];
  if (provenance.baseArtefactSha256) {
    bits.push(` data-base-artefact-sha256="${esc(provenance.baseArtefactSha256)}"`);
  }
  if (provenance.compareArtefactSha256) {
    bits.push(` data-compare-artefact-sha256="${esc(provenance.compareArtefactSha256)}"`);
  }
  if (provenance.commit) bits.push(` data-commit="${esc(provenance.commit)}"`);
  bits.push('>');
  return bits.join('');
}

function title(
  diff: DiffResult,
  sheet: PageAlignmentEntry,
  counts: Record<ChangeClass, number>,
): string {
  const where =
    sheet.basePageIndex === null
      ? `new sheet ${(sheet.comparePageIndex ?? 0) + 1}`
      : sheet.comparePageIndex === null
        ? `sheet ${sheet.basePageIndex + 1}, removed`
        : `sheet ${sheet.basePageIndex + 1} to ${sheet.comparePageIndex + 1}`;
  const summary = DIFF_LEGEND_ORDER.filter((c) => counts[c] > 0)
    .map((c) => `${counts[c]} ${DIFF_STYLE[c].label.en}`)
    .join(', ');
  return `<title>${esc(`Comparison overlay — ${where} — ${summary || 'nothing on this sheet'}`)}</title>`;
}

function description(
  diff: DiffResult,
  sheet: PageAlignmentEntry,
  provenance: DiffOverlayProvenance,
): string {
  const lines = [
    `Drawing comparison, matcher profile ${diff.matcherProfileVersion}, contract ${diff.schemaVersion}.`,
    `Compared in ${diff.comparisonSpace === 'drawing_mm' ? 'drawing millimetres' : 'normalised page units'}` +
      `${diff.partial ? ', partial because physical scale was not determined on one or both sides' : ''}.`,
    `Matching tolerance ${f(diff.tolerance)} ${diff.displacementUnit === 'mm' ? 'mm' : 'page diagonals'}.`,
    `Base sheet ${sheet.basePageIndex === null ? 'none' : sheet.basePageIndex + 1},` +
      ` compare sheet ${sheet.comparePageIndex === null ? 'none' : sheet.comparePageIndex + 1}.`,
    provenance.baseArtefactSha256
      ? `Base artefact ${provenance.baseArtefactSha256.slice(0, 16)}, compare artefact ${(provenance.compareArtefactSha256 ?? '').slice(0, 16)}.`
      : '',
    'Class is encoded by line type, line weight and badge shape; colour is redundant.',
  ].filter((s) => s !== '');
  return `<desc>${esc(lines.join(' '))}</desc>`;
}

function pageBoxOutline(page: DrawingPage, other: DrawingPage | null): string {
  // Base geometry is never rescaled to the compare sheet's box. Rescaling would
  // turn a change of paper size into apparent movement of every entity, so the
  // old sheet's edge is drawn instead and the reader is told which is which.
  const label = `old sheet ${f(page.widthPt)} x ${f(page.heightPt)} pt` +
    (other === null ? '' : `, new sheet ${f(other.widthPt)} x ${f(other.heightPt)} pt`);
  return (
    `<rect class="d2d-diff-pagebox" data-role="pagebox" data-side="base" x="0" y="0"` +
    ` width="${f(page.widthPt)}" height="${f(page.heightPt)}"><title>${esc(label)}</title></rect>` +
    `<text class="d2d-diff-pagebox-label" data-role="pagebox" x="${f(2)}" y="${f(page.heightPt - 2)}"` +
    ` font-size="${f(Math.max(page.heightPt / 90, 5))}">${esc(label)}</text>`
  );
}

function styleSheet(): string {
  const rules: string[] = [
    '.d2d-diff-paper{fill:#ffffff}',
    '.d2d-diff-pagebox{fill:none;stroke:#8a949e;stroke-width:1;stroke-dasharray:7 2.1 1.4 2.1;vector-effect:non-scaling-stroke}',
    '.d2d-diff-pagebox-label{fill:#5c6672;stroke:none;font-family:ui-sans-serif,system-ui,sans-serif}',
    '.d2d-d{fill:none;vector-effect:non-scaling-stroke}',
    '.d2d-t{stroke:none}',
    '.d2d-ghost{fill:none;vector-effect:non-scaling-stroke;stroke-width:' +
      GHOST.strokeWidth +
      ';stroke-dasharray:' +
      GHOST.dasharray +
      ';opacity:' +
      GHOST.opacity +
      '}',
    '.d2d-ghost-text{stroke:none;opacity:' + GHOST.opacity + '}',
    '.d2d-leader{fill:none;stroke-width:0.5;vector-effect:non-scaling-stroke}',
    '.d2d-badge{stroke-width:1;vector-effect:non-scaling-stroke}',
    '.d2d-glyph{fill:none;stroke-width:1.2;stroke-linecap:round;vector-effect:non-scaling-stroke}',
    '.d2d-glyph-filled{stroke:none}',
    '.d2d-hit{fill:none;stroke:transparent;stroke-width:6;pointer-events:stroke;vector-effect:non-scaling-stroke}',
    '.d2d-hit-box{fill:transparent;stroke:none;pointer-events:all}',
    '.d2d-legend-panel{fill:#ffffff;stroke:#dde3ea;stroke-width:1}',
    '.d2d-legend text{font-family:ui-sans-serif,system-ui,sans-serif;fill:#14181d}',
    '.d2d-legend .d2d-legend-note{fill:#5c6672}',
    '.d2d-legend .d2d-legend-banner{fill:#14181d;font-weight:700}',
    '.d2d-legend-banner-ground{fill:#f4f6f9;stroke:#14181d;stroke-width:0.6}',
  ];

  for (const changeClass of CHANGE_CLASS_LIST) {
    const s = DIFF_STYLE[changeClass];
    const dash = s.dasharray === '' ? '' : `stroke-dasharray:${s.dasharray};`;
    const cap = s.linecap === 'round' ? 'stroke-linecap:round;' : '';
    rules.push(
      `.d2d-d-${changeClass}{stroke:${s.colour};stroke-width:${s.strokeWidth};${dash}${cap}}`,
      `.d2d-t-${changeClass}{fill:${s.colour}}`,
      `.d2d-ghost-${changeClass}{stroke:${s.colour}}`,
      `.d2d-ghost-text.d2d-ghost-${changeClass}{fill:${s.colour};stroke:none}`,
      `.d2d-leader-${changeClass}{stroke:${s.colour}}`,
      `.d2d-head-${changeClass}{fill:${s.colour};stroke:none}`,
      `.d2d-badge-${changeClass}{fill:#ffffff;stroke:${s.colour}}`,
      `.d2d-glyph-${changeClass}{stroke:${s.colour}}`,
      `.d2d-glyph-filled.d2d-glyph-${changeClass}{fill:${s.colour}}`,
    );
  }

  // Printed, or opened under forced colours, every hue collapses to one ink.
  // The six classes have to remain separable then, which is the whole reason
  // the encoding does not rest on colour. Stating it here makes the claim
  // executable rather than aspirational.
  rules.push(
    '@media print{.d2d-diff .d2d-d,.d2d-diff .d2d-badge,.d2d-diff .d2d-glyph,.d2d-diff .d2d-leader{stroke:#14181d}' +
      '.d2d-diff .d2d-t,.d2d-diff .d2d-head,.d2d-diff .d2d-glyph-filled{fill:#14181d}}',
    '@media (forced-colors:active){.d2d-diff .d2d-d,.d2d-diff .d2d-badge,.d2d-diff .d2d-glyph,.d2d-diff .d2d-leader{stroke:CanvasText}' +
      '.d2d-diff .d2d-t,.d2d-diff .d2d-head,.d2d-diff .d2d-glyph-filled{fill:CanvasText}}',
  );
  return rules.join('');
}

const CHANGE_CLASS_LIST: readonly ChangeClass[] = DIFF_LEGEND_ORDER;

/**
 * Breaks a banner onto lines that fit the legend.
 *
 * The banners are the five edge states, and they are the sentences a reader
 * most needs whole — "distances are normalised page units, not millimetres" is
 * not a message to truncate. Wrapping on words rather than shrinking the type
 * keeps them legible when the file is printed.
 */
function wrap(text: string, columns: number): string[] {
  const lines: string[] = [];
  let current = '';
  for (const word of text.split(' ')) {
    if (current === '') current = word;
    else if (current.length + 1 + word.length <= columns) current = `${current} ${word}`;
    else {
      lines.push(current);
      current = word;
    }
  }
  if (current !== '') lines.push(current);
  return lines;
}

function countByClass(entries: readonly DiffEntry[]): Record<ChangeClass, number> {
  const out = Object.fromEntries(DIFF_LEGEND_ORDER.map((c) => [c, 0])) as Record<ChangeClass, number>;
  for (const e of entries) out[e.changeClass] += 1;
  return out;
}

/**
 * The legend the file carries for a reviewer who opens it alone.
 *
 * It names each line type **in words**, which is the part that matters: a
 * specimen stroke is useless once the file is printed in greyscale, photocopied
 * or read aloud, and a name survives all three. Both counts are shown, this
 * sheet and the whole drawing, because a standalone file shows one sheet and a
 * reader who sees one number will take it for the other.
 */
function legend(
  diff: DiffResult,
  sheetCounts: Record<ChangeClass, number>,
  edges: readonly DiffEdge[],
  width: number,
  height: number,
): string {
  const rowHeight = 20;
  const banners = edges.flatMap((edge) => wrap(edgeMessage(edge, diff, 'en'), 74));
  const innerWidth = 360;
  const innerHeight = 52 + banners.length * 11 + (DIFF_LEGEND_ORDER.length + 1) * rowHeight + 26;

  const panelWidth = width * 0.44;
  const panelHeight = (panelWidth * innerHeight) / innerWidth;
  const margin = width * 0.02;

  const rows: string[] = [];
  let y = 42 + banners.length * 11;

  const drawRow = (
    specimen: { colour: string; strokeWidth: number; dasharray: string; linecap: string; opacity?: number },
    badgeShape: DiffClassStyle['badge'],
    colour: string,
    label: string,
    pattern: string,
    counts: string,
  ): void => {
    const dash = specimen.dasharray === '' ? '' : ` stroke-dasharray="${specimen.dasharray}"`;
    const cap = specimen.linecap === 'round' ? ' stroke-linecap="round"' : '';
    const opacity = specimen.opacity === undefined ? '' : ` opacity="${specimen.opacity}"`;
    rows.push(
      `<line x1="8" y1="${y}" x2="46" y2="${y}" stroke="${specimen.colour}"` +
        ` stroke-width="${specimen.strokeWidth}"${dash}${cap}${opacity}/>`,
    );
    if (badgeShape !== 'none') {
      const bx = 56;
      const r = 5;
      rows.push(
        badgeShape === 'circle'
          ? `<circle cx="${bx}" cy="${y}" r="${r}" fill="#ffffff" stroke="${colour}" stroke-width="1"/>`
          : badgeShape === 'square'
            ? `<rect x="${bx - r}" y="${y - r}" width="${2 * r}" height="${2 * r}" fill="#ffffff" stroke="${colour}" stroke-width="1"/>`
            : `<polygon points="${bx},${y - r} ${bx + r},${y} ${bx},${y + r} ${bx - r},${y}" fill="#ffffff" stroke="${colour}" stroke-width="1"/>`,
      );
    }
    rows.push(`<text x="68" y="${y + 3}" font-size="9">${esc(label)}</text>`);
    rows.push(`<text x="68" y="${y + 13}" font-size="7.5" class="d2d-legend-note">${esc(pattern)}</text>`);
    rows.push(`<text x="352" y="${y + 3}" font-size="8" text-anchor="end">${esc(counts)}</text>`);
    y += rowHeight;
  };

  for (const changeClass of DIFF_LEGEND_ORDER) {
    const s = DIFF_STYLE[changeClass];
    drawRow(
      s,
      s.badge,
      s.colour,
      `${s.label.en} ${s.label.ja}`,
      `${s.patternName.en} / ${s.patternName.ja}`,
      `${sheetCounts[changeClass]} here · ${diff.summary.byClass[changeClass]} in all`,
    );
  }
  drawRow(
    { colour: DIFF_STYLE.changed.colour, strokeWidth: GHOST.strokeWidth, dasharray: GHOST.dasharray, linecap: 'butt', opacity: GHOST.opacity },
    'none',
    DIFF_STYLE.changed.colour,
    `${GHOST.label.en} ${GHOST.label.ja}`,
    `${GHOST.patternName.en} / ${GHOST.patternName.ja}`,
    '',
  );

  const bannerMarkup = banners.flatMap((text, i) => [
    `<rect class="d2d-legend-banner-ground" x="6" y="${26 + i * 11}" width="348" height="11"/>`,
    `<text class="d2d-legend-banner" x="10" y="${34 + i * 11}" font-size="7">${esc(text)}</text>`,
  ]);

  return [
    `<svg class="d2d-legend" x="${f(margin)}" y="${f(height - panelHeight - margin)}"` +
      ` width="${f(panelWidth)}" height="${f(panelHeight)}" viewBox="0 0 ${innerWidth} ${innerHeight}">`,
    `<rect class="d2d-legend-panel" x="0.5" y="0.5" width="${innerWidth - 1}" height="${innerHeight - 1}"/>`,
    '<text x="8" y="12" font-size="7.5" class="d2d-legend-note">' +
      esc('circle = in the new version · square = in the old version only · diamond = not decided') +
      '</text>',
    '<text x="8" y="21" font-size="7.5" class="d2d-legend-note">' +
      esc('丸＝新版にある・角＝旧版のみ・菱形＝未判定') +
      '</text>',
    ...bannerMarkup,
    ...rows,
    `<text x="8" y="${innerHeight - 14}" font-size="7.5" class="d2d-legend-note">` +
      esc('Class is encoded by line type, line weight and badge shape. Colour is redundant.') +
      '</text>',
    `<text x="8" y="${innerHeight - 5}" font-size="7.5" class="d2d-legend-note">` +
      esc('分類は線種・線幅・記号で表しています。色は補助です。') +
      '</text>',
    '</svg>',
  ].join('\n');
}
